import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { surveyReportApi } from '../../survey-report/api/surveyReportApi';
import { useNotifications } from '@shared/components/notifications/useNotifications';

// Query keys
export const auditReportKeys = {
  reportByAuditId: (auditId: string) => ['audit-report', auditId] as const,
};

// Hook to check if a report exists for an audit
export const useAuditReport = (auditId: string) => {
  return useQuery({
    queryKey: auditReportKeys.reportByAuditId(auditId),
    queryFn: () => surveyReportApi.getReportByAuditId(auditId),
    retry: false, // Don't retry on 404 - means report doesn't exist
    staleTime: 30000, // Consider data fresh for 30 seconds
  });
};

// Hook to generate a report from an audit
export const useGenerateReportFromAudit = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: ({
      auditId,
      data,
    }: {
      auditId: string;
      data?: {
        title?: string;
        description?: string;
        options?: any;
      };
    }) => surveyReportApi.generateFromAudit(auditId, data),
    onSuccess: (response, variables) => {
      // Invalidate the audit report query to refresh the UI
      queryClient.invalidateQueries({
        queryKey: auditReportKeys.reportByAuditId(variables.auditId),
      });
      success('Report generation started successfully');
    },
    onError: (err: Error) => {
      const message =
        err instanceof AxiosError ? err.response?.data?.message || err.message : err.message;
      error(message || 'Failed to generate report');
    },
  });
};

// Helper hook to determine if audit should show generate report button
export const useAuditReportStatus = (auditId: string, auditStatus: string, isLocked: boolean) => {
  const { data: reportQuery, isLoading, error } = useAuditReport(auditId);

  const reportExists = reportQuery?.data != null;
  const reportNotFound = error instanceof AxiosError && error.response?.status === 404;

  // Show generate report button if:
  // 1. Audit is completed and published
  // 2. Report doesn't exist (404 error or no data)
  // 3. Not currently loading
  const shouldShowGenerateButton =
    auditStatus === 'PUBLISHED' && isLocked && !reportExists && !isLoading;

  return {
    reportExists,
    reportNotFound,
    shouldShowGenerateButton,
    isLoading,
    reportData: reportQuery?.data,
  };
};
